import { useMemo } from "react";
import type { CopyNurses } from "@utils/rosterCreate-util";

type NurseWithPreceptor = CopyNurses[number] & {
  readonly preceptor?: { readonly role?: string | null } | null;
  readonly isAppliedMap?: Record<number, boolean>;
};

type UseRosterCreateDerivedNursesParams = {
  readonly copyNursesWithWanted: CopyNurses;
  readonly includePrecepteeInValidationCount: boolean;
  readonly includePrecepteeInStatsCount: boolean;
  // 대상 월 기준 프리셉티 '일(1-based)' 집합(간호사별). 없으면 프리셉티 제외 없음.
  // 캐시(preceptor_id/role)가 아니라 assignment 기간(period) 기반 → 종료 후 자동 카운트.
  readonly precepteeDaysByNurseId?: Map<string, Set<number>>;
};

export function useRosterCreateDerivedNurses({
  copyNursesWithWanted,
  includePrecepteeInValidationCount,
  includePrecepteeInStatsCount,
  precepteeDaysByNurseId,
}: UseRosterCreateDerivedNursesParams) {
  // 프리셉티는 '제거'가 아니라 프리셉티-day 셀만 null 처리 → 그 날만 카운트에서 빠지고
  // 기간 종료 후(다음달/월중 종료 이후)엔 정상 카운트된다.
  const validationCopyNurses = useMemo(() => {
    return copyNursesWithWanted.map((nurse) => {
      const withAppliedMap = nurse as NurseWithPreceptor;
      const pteDays = includePrecepteeInValidationCount
        ? undefined
        : precepteeDaysByNurseId?.get(nurse.nurse_id);
      const hasAppliedMap = !!withAppliedMap.isAppliedMap;
      if (!(pteDays && pteDays.size > 0) && !hasAppliedMap) return nurse;
      return {
        ...nurse,
        schedule:
          nurse.schedule?.map((shift, idx) => {
            const day = idx + 1;
            if (pteDays?.has(day)) return null; // 프리셉티 기간 셀 제외
            if (hasAppliedMap && withAppliedMap.isAppliedMap?.[day] === false)
              return null; // 거절된 원티드 투명화(기존 동작)
            return shift;
          }) ?? null,
      };
    });
  }, [
    copyNursesWithWanted,
    includePrecepteeInValidationCount,
    precepteeDaysByNurseId,
  ]);

  const statsNurses = useMemo(() => {
    if (includePrecepteeInStatsCount) return copyNursesWithWanted;
    return copyNursesWithWanted.map((nurse) => {
      const pteDays = precepteeDaysByNurseId?.get(nurse.nurse_id);
      if (!pteDays || pteDays.size === 0) return nurse; // 그 달 프리셉티 아님 → 그대로 카운트
      return {
        ...nurse,
        schedule:
          nurse.schedule?.map((shift, idx) =>
            pteDays.has(idx + 1) ? null : shift
          ) ?? null,
      };
    });
  }, [
    copyNursesWithWanted,
    includePrecepteeInStatsCount,
    precepteeDaysByNurseId,
  ]);

  return { validationCopyNurses, statsNurses };
}
